import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { NbDialogRef, NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { CreditBalance, CreditService } from '../../../services/credit.service';

@Component({
  selector: 'app-buy-credits-dialog',
  templateUrl: './buy-credits-dialog.component.html',
  styleUrl: './buy-credits-dialog.component.scss',
})
export class BuyCreditsDialogComponent implements OnInit {
  @Input() balance?: CreditBalance;

  submitting = false;
  quickPicks = [10, 25, 50, 100];

  form = this.fb.group({
    eur_amount: [10, [Validators.required, Validators.min(10), Validators.pattern(/^\d+$/)]],
  });

  constructor(
    public dialogRef: NbDialogRef<BuyCreditsDialogComponent>,
    private fb: FormBuilder,
    private creditService: CreditService,
    private toastr: NbToastrService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    const min = this.balance?.min_purchase_eur ?? 10;
    const max = this.balance?.max_purchase_eur ?? 10000;
    this.form.controls.eur_amount.setValidators([
      Validators.required,
      Validators.min(min),
      Validators.max(max),
      // Whole EUR only — no cents. Stripe enforces this again via
      // adjustable_quantity, this is just the earlier, friendlier error.
      Validators.pattern(/^\d+$/),
    ]);
    this.form.controls.eur_amount.setValue(min);
    this.quickPicks = this.quickPicks.filter((v) => v >= min && v <= max);
  }

  get minEur(): number {
    return this.balance?.min_purchase_eur ?? 10;
  }

  get maxEur(): number {
    return this.balance?.max_purchase_eur ?? 10000;
  }

  get eurValue(): number {
    const raw = this.form.controls.eur_amount.value;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  /** Live preview computed from the constants shipped with /credits/balance —
   *  no request per keystroke. The grant uses the purchase row's own snapshot,
   *  so a stale constant here can only mis-display, never mis-grant. */
  get previewCredits(): number {
    return this.eurValue * (this.balance?.credits_per_eur ?? 0);
  }


  pick(value: number) {
    this.form.controls.eur_amount.setValue(value);
    this.form.controls.eur_amount.markAsDirty();
  }

  buy() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.submitting = true;
    this.creditService.createCheckout(this.eurValue).subscribe({
      next: (data) => {
        // Same handoff as the plan checkout (product-card): leave the SPA and
        // let Stripe host the payment page.
        window.open(data.payment_link, '_self');
      },
      error: (err) => {
        this.submitting = false;
        const detail = err?.error?.detail;
        this.toastr.danger(
          detail || this.translate.instant('pages.organisation.credits.buyDialog.error'),
          this.translate.instant('pages.organisation.credits.toastErrorTitle')
        );
      },
    });
  }

  close() {
    this.dialogRef.close();
  }
}
